<template>
  <a
    class="promo-card"
    :href="promoCard.targetUrl"
    v-data-test-id="'promo-card'"
    @click="trackClick"
  >
    <ImageContentful
      :src="promoCard.imageUrl"
      containerSelector=".promo-card__image"
      :isBackgroundImage="true"
      :options="{ setHeight: false }"
      class="promo-card__image"
    />
    <div class="promo-card__content">
      <div class="promo-card__text" :class="'promo-card__text--' + promoCard.variant">
        {{ promoCard.text }}
      </div>
      <button class="promo-card__cta" :class="'promo-card__cta--' + promoCard.variant">
        {{ promoCard.ctaText }}
      </button>
    </div>
  </a>
</template>

<script>
import { observeIntersection } from '@/helpers/intersection-observer'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'PromoCard',
  components: { ImageContentful },

  props: {
    promoCard: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    onCardView: {
      type: Function,
    },

    onCardClick: {
      type: Function,
      default() {
        return () => {}
      },
    },
  },

  computed: {
    ...mapGetters({ currentBreakpoint: 'filter/currentBreakpoint' }),

    position() {
      return Number(this.currentBreakpoint?.promoPositions?.indexOf(this.index)) + 1
    },
  },

  mounted() {
    if (this.onCardView) this.addIntersectionObserver()
  },

  methods: {
    addIntersectionObserver() {
      if (!window.IntersectionObserver) return

      const onIntersection = () => this.onCardView(this.promoCard, this.position)
      observeIntersection(this.$el, onIntersection, { threshold: 0.7 })
    },

    trackClick() {
      this.onCardClick(this.promoCard, this.position)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './card-basic';

.promo-card {
  @extend %card-basic;
  min-height: 600px;

  &:hover {
    .promo-card__cta--dark {
      background-color: $color-grey-dark-1;
    }

    .promo-card__cta--light {
      background-color: rgba($color-white, 0.85);
    }

    .promo-card__cta--transparent {
      background-color: rgba($color-white, 0.1);
    }
  }

  &__image {
    flex: 1;
    background-size: cover;
    background-position: center center;
  }

  &__content {
    position: absolute;
    bottom: 0;
  }

  &__text {
    font-family: $font-family-neutraface;
    font-size: $font-size-heading-large;
    color: $color-grey-dark-2;
    letter-spacing: $font-spacing-neutraface;
    line-height: $line-height-small;
    margin: 0 40px;

    &--light,
    &--transparent {
      color: $color-white;
    }
  }

  &__cta {
    margin: 30px 40px 40px;
    padding: 17px 24px 16px;
    line-height: 1;
    color: $color-white;
    background-color: $color-grey-dark-2;
    transition: background-color $transition-time ease-in-out;

    &--transparent {
      background-color: transparent;
      border: 2px solid $color-white;
    }

    &--light {
      color: $color-grey-dark-2;
      background-color: $color-white;
    }
  }

  .site-search__products & {
    max-width: calc(100% - 25px);
    margin: 0 15px 15px;

    @media (min-width: 680px) {
      max-width: calc(50% - 35px);
      margin: 0 0 20px 25px;
    }

    @media (min-width: 1000px) {
      max-width: calc(33% - 30px);
      margin: 0 15px 30px;
    }

    @media (min-width: 1450px) {
      max-width: calc(25% - 16px);
      margin: 0 8px 16px;
    }

    @media (min-width: 1700px) {
      max-width: calc(20% - 16px);
      margin: 0 8px 16px;
    }
  }
}
</style>
